/**
 * @typedef {Object} FieldRule
 * @property {"min"|"max"|"range"} type - Which RULE_BUILDERS entry validates the field.
 * @property {number} [value] - Threshold for "min"/"max".
 * @property {number} [min] - Inclusive lower bound for "range".
 * @property {number} [max] - Inclusive upper bound for "range".
 * @property {number} [exclusiveMin] - Exclusive lower bound for "range".
 * @property {number} [exclusiveMax] - Exclusive upper bound for "range".
 * @property {string} message - Shown when the value fails this rule.
 */

const RULE_BUILDERS = {
  min: rule => value => Number(value) >= rule.value || rule.message,
  max: rule => value => Number(value) <= rule.value || rule.message,
  range: rule => value => {
    const number = Number(value);
    if (rule.min !== undefined && number < rule.min) return rule.message;
    if (rule.max !== undefined && number > rule.max) return rule.message;
    if (rule.exclusiveMin !== undefined && number <= rule.exclusiveMin) return rule.message;
    if (rule.exclusiveMax !== undefined && number >= rule.exclusiveMax) return rule.message;
    return true;
  },
};

/**
 * Turns a field descriptor's declarative `rules` (data, not code — so a backend-served schema
 * can carry them) into the validator-function array RfTextInput's `rules` prop expects.
 *
 * @param {import("./adminSections").NumberFieldDescriptor} descriptor - Field descriptor.
 * @returns {((value: *) => true | string)[]} Validator functions.
 * @example
 * buildFieldRules({ rules: [{ type: "min", value: 1000, message: "Minimum is 1,000" }] });
 */
export const buildFieldRules = descriptor =>
  (descriptor.rules ?? []).map(rule => {
    const build = RULE_BUILDERS[rule.type];
    if (!build) throw new Error(`Unknown field validation rule type: "${rule.type}"`);
    return build(rule);
  });
